<template>
  <v-card>
    <v-card-text class="py-0">
      <v-row dense>
        <v-col>
          <v-card-title class="px-0">
            {{ title }}
          </v-card-title>
          <report-sub-title
            :filter="filter"
            :team="team"
            :manager="manager"
            :user="user"
          ></report-sub-title>
        </v-col>
        <v-spacer class="limited-spacer"></v-spacer>
        <v-col
          class="my-auto"
          :cols="$vuetify.breakpoint.smAndDown ? '12' : 'auto'"
          v-if="trendTypes.length"
        >
          <v-select
            label="Trend Type"
            v-model="filter.trendIds"
            outlined
            dense
            hide-details
            :items="trendTypes"
            item-text="name"
            item-value="id"
            class="min-width"
            @change="refreshData"
            multiple
          >
            <template v-slot:prepend-item>
              <v-list-item ripple @click="toggleTrendIds">
                <v-list-item-action>
                  <v-icon :color="filter.trendIds.length > 0 ? 'darken-4' : ''">
                    {{ icon }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    Select All
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
            <template v-slot:selection="{ item, index }">
              <template v-if="index === 0">
                <template v-if="filter.trendIds.length > 1">
                  {{ filter.trendIds.length }} selected
                </template>
                <template v-else>
                  {{ item.name }}
                </template>
              </template>
            </template>
          </v-select>
        </v-col>
        <v-col
          class="my-auto"
          :cols="$vuetify.breakpoint.smAndDown ? '12' : 'auto'"
          v-if="
            isAdminOrManager &&
              enableBusinessMetrics &&
              businessMetrics.length > 1
          "
        >
          <v-select
            label="Business Metric"
            v-model="businessMetricId"
            outlined
            dense
            hide-details
            :items="businessMetrics"
            item-text="name"
            item-value="id"
            class="min-width"
          >
          </v-select>
        </v-col>
        <v-col
          :class="'my-auto' + (showChangeFilter ? '' : ' pr-4')"
          :cols="$vuetify.breakpoint.smAndDown ? '12' : 'auto'"
        >
          <timeframe-select
            v-model="filter"
            v-on:change="refreshData"
          ></timeframe-select>
        </v-col>
        <v-col
          class="my-auto pr-4"
          :cols="$vuetify.breakpoint.smAndDown ? '12' : 'auto'"
          v-if="showChangeFilter"
        >
          <v-btn outlined color="secondary" @click="showChange">
            Change Filter
            <v-icon class="ml-2">filter_list</v-icon>
          </v-btn>
          <change-filter-dialog
            ref="change"
            v-on:save="$emit('change', $event)"
          ></change-filter-dialog>
        </v-col>
      </v-row>
    </v-card-text>
    <slot></slot>
    <template v-if="showTable">
      <v-divider></v-divider>
      <v-card-text>
        <report-table
          :filter="filter"
          :headers="headers"
          :hideAnonymous="hideAnonymous"
          :loading="loading"
          :items="items"
          v-on:rowClick="rowClick"
        >
        </report-table>
      </v-card-text>
    </template>
  </v-card>
</template>

<script>
import TimeframeSelect from "@/components/reporting/TimeframeSelect";
import ReportTable from "@/components/reporting/ReportTable";
import ReportSubTitle from "@/components/reporting/ReportSubTitle";
import reports from "@/helpers/mixins/reports";
import ChangeFilterDialog from "@/components/reporting/ChangeFilterDialog";
import { mapGetters } from "vuex";
export default {
  name: "Report",
  props: {
    filter: { type: Object },
    team: { type: Object },
    manager: { type: Object },
    user: { type: Object },
    title: { type: String },
    headers: { type: Array },
    hideAnonymous: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    items: { type: Array },
    showTable: { type: Boolean, default: true },
    showChangeFilter: { type: Boolean, default: false },
    enableBusinessMetrics: { type: Boolean, default: false }
  },
  components: {
    TimeframeSelect,
    ReportSubTitle,
    ChangeFilterDialog,
    ReportTable
  },
  data: () => ({}),
  mixins: [reports],
  computed: {
    isAdminOrManager() {
      return this.is_admin || this.is_manager;
    },
    businessMetricId: {
      get() {
        return this.business_metric_id;
      },
      set(val) {
        this.setBusinessMetricData(val);
      }
    },
    businessMetrics() {
      return [{ id: null, name: "None" }].concat(this.business_metrics);
    },
    trendTypes() {
      let data = this._.orderBy(
        this.trend_types
          .filter(x => x.coreCardTypeId == this.filter.coreCardTypeId)
          .map(x => ({ id: x.id, name: this.t2(x.name) })),
        ["name"],
        ["asc"]
      );
      if (!data.length) {
        return [];
      }
      data.unshift({ id: null, name: "Overall" });
      return data;
    },
    allTrendTypes() {
      return this.filter.trendIds.length === this.trendTypes.length;
    },
    someTrendTypes() {
      return this.filter.trendIds.length > 0 && !this.allTrendTypes;
    },
    icon() {
      if (this.allTrendTypes) return "mdi-close-box";
      if (this.someTrendTypes) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },
    ...mapGetters({
      business_metric_id: "reporting/business_metric_id",
      business_metrics: "company/business_metrics",
      is_admin: "profile/is_admin",
      is_manager: "profile/is_manager",
      trend_types: "reporting/trend_types"
    })
  },
  methods: {
    toggleTrendIds() {
      this.$nextTick(() => {
        if (this.allTrendTypes) {
          this.filter.trendIds = [];
        } else {
          this.filter.trendIds = this.trendTypes.map(x => x.id);
        }
        this.refreshData();
      });
    },
    showChange() {
      this.$refs.change.showDialog(this.filter);
    },
    rowClick(item) {
      this.$emit("rowClick", item);
    },
    refreshData() {
      this.$emit("refreshData");
      this.setBusinessMetricData(this.business_metric_id);
    },
    setBusinessMetricData(val) {
      //this.list_loading = true;
      Promise.all([
        this.$store.dispatch("reporting/setBusinessMetricId", val),
        this.$store.dispatch(
          "reporting/getBusinessMetricTrend",
          Object.assign({}, this.filter, {
            businessMetricId: val
          })
        )
      ])
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading report."
          );
        })
        .finally(() => {
          //this.list_loading = false;
        });
    }
  }
};
</script>
